import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCurrency } from "@/context/CurrencyContext";
import { Sparkles, Wallet, ArrowUpRight, ArrowDownRight, Gift, TrendingUp, Clock } from "lucide-react";

const Shimmer = () => <div className="grid sm:grid-cols-2 gap-4">{[1, 2].map((i) => <div key={i} className="h-32 rounded-2xl shimmer" />)}</div>;

export const BuyerWallet = () => {
  const { format } = useCurrency();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/buyer/wallet").then((r) => setData(r.data)).catch(() => setData({ points_balance: 0, credit_balance: 0, points_history: [], credit_history: [] })); }, []);

  if (!data) return <Shimmer />;

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Loyalty card */}
        <div data-testid="loyalty-balance-card" className="rounded-2xl p-6 text-white bg-gradient-to-br from-orange-500 to-amber-500 shadow-[0_10px_30px_rgba(234,88,12,0.28)] relative overflow-hidden">
          <Sparkles className="w-24 h-24 absolute -right-4 -bottom-4 opacity-15" />
          <div className="flex items-center gap-2 text-sm opacity-90"><Sparkles className="w-4 h-4" />Loyalty Points</div>
          <p className="text-4xl font-black font-display mt-2">{data.points_balance}</p>
          <p className="text-xs opacity-90 mt-1">Earn 1 point for every €1 you spend</p>
          <div className="flex gap-4 mt-4 text-xs">
            <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />{data.points_earned ?? 0} earned</span>
            <span className="flex items-center gap-1"><ArrowDownRight className="w-3.5 h-3.5" />{data.points_redeemed ?? 0} redeemed</span>
          </div>
        </div>
        {/* Store credit card */}
        <div data-testid="credit-balance-card" className="rounded-2xl p-6 bg-white dark:bg-card shadow-[0_4px_20px_rgba(0,0,0,0.06)] relative overflow-hidden border">
          <Wallet className="w-24 h-24 absolute -right-4 -bottom-4 opacity-[0.06] text-primary" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Wallet className="w-4 h-4 text-primary" />Store Credit</div>
          <p className="text-4xl font-black font-display mt-2 text-primary">{format(data.credit_balance)}</p>
          <p className="text-xs text-muted-foreground mt-1">Applied automatically at checkout</p>
          {data.credit_expires_at && data.credit_balance > 0 && (() => {
            const days = Math.ceil((new Date(data.credit_expires_at) - new Date()) / 86400000);
            const soon = days <= 30;
            return (
              <div data-testid="credit-expiry-note" className={`mt-3 inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 ${soon ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400" : "bg-muted text-muted-foreground"}`}>
                <Clock className="w-3.5 h-3.5" />
                {days > 0 ? `Expires in ${days} day${days !== 1 ? "s" : ""} · ${new Date(data.credit_expires_at).toLocaleDateString()}` : "Expired"}
              </div>
            );
          })()}
        </div>
      </div>

      <Tabs defaultValue="points">
        <TabsList data-testid="wallet-tabs">
          <TabsTrigger value="points" data-testid="wallet-tab-points">Points history</TabsTrigger>
          <TabsTrigger value="credit" data-testid="wallet-tab-credit">Credit history</TabsTrigger>
        </TabsList>

        <TabsContent value="points" className="mt-4">
          <Card className="rounded-2xl divide-y divide-border/50">
            {data.points_history.length === 0 && <p className="p-8 text-center text-muted-foreground text-sm">No points earned yet — start shopping!</p>}
            {data.points_history.map((e) => (
              <div key={e.entry_id} data-testid={`points-row-${e.entry_id}`} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center ${e.type === "earned" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-600"}`}>
                    {e.type === "earned" ? <ArrowUpRight className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
                  </span>
                  <div><p className="text-sm font-medium">{e.reason}</p><p className="text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</p></div>
                </div>
                <span className={`font-bold text-sm ${e.type === "earned" ? "text-emerald-600" : "text-amber-600"}`}>{e.type === "earned" ? "+" : "-"}{e.points} pts</span>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="credit" className="mt-4">
          <Card className="rounded-2xl divide-y divide-border/50">
            {data.credit_history.length === 0 && <p className="p-8 text-center text-muted-foreground text-sm">No store credit activity yet.</p>}
            {data.credit_history.map((e) => (
              <div key={e.entry_id} data-testid={`credit-row-${e.entry_id}`} className="flex items-center justify-between p-4">
                <div><p className="text-sm font-medium">{e.reason}</p><p className="text-[11px] text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</p></div>
                <span className={`font-bold text-sm ${e.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>{e.type === "credit" ? "+" : "-"}{format(e.amount)}</span>
              </div>
            ))}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
